import { useMyBubblesQuery } from "@crowdcoursing/api/graphql";
import { Box, CircularProgress, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useEventDashboard } from "~hooks/useEventDashboard";
import { useEventJoin } from "~hooks/useEventJoin";
import { useEventMeetingTimes } from "~hooks/useEventMeetingTimes";
import { useEventRules } from "~hooks/useEventRules";
import { useEventSeries } from "~hooks/useEventSeries";
import { useEventSlideouts } from "~hooks/useEventSlideouts";
import { type State, useSelector } from "~hooks/useTypedRedux";
import { useWrappedChat } from "~hooks/useWrappedChat";
import { ConfirmCancelRequestDialog } from "~ui/molecules/ConfirmCancelRequestDialog/ConfirmCancelRequestDialog";
import { ConfirmDeleteDialog } from "~ui/molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";
import { EventRulesAgreementModal } from "~ui/molecules/EventRulesAgreementModal/EventRulesAgreementModal";
import { EventRulesDialog } from "~ui/molecules/EventRulesDialog/EventRulesDialog";
import { EventSeriesDialog } from "~ui/molecules/EventSeriesDialog/EventSeriesDialog";
import { EventDashboardTemplate } from "~ui/templates/EventDashboardTemplate/EventDashboardTemplate";

// User-facing strings
const STRINGS = {
  ERROR_LOADING: "Error loading event",
  EVENT_NOT_FOUND: "Event not found",
  FROM: "from",
} as const;

export const EventDashboardPage = () => {
  const { eventId = "" } = useParams();
  const userId = useSelector((state: State) => state.user?.id) || "";
  const { data: myBubbles } = useMyBubblesQuery();

  const {
    event,
    isLoading,
    error,
    imageUrl,
    status,
    uploadImage,
    memberData,
    resourceManagement,
    handleRsvp,
    handleUnrsvp,
    handleRequestJoin,
    handleLeave,
    handleCancelRequest,
    confirmCancelRequest,
    isCancelRequestDialogOpen,
    setIsCancelRequestDialogOpen,
    handleShareEvent,
    handleAddMember,
    handleRemoveMember,
    handleApproveMember,
    handleRejectMember,
    handleAddFacilitator,
    handleRemoveFacilitator,
    handleAddOwner,
    pendingRemovalMember,
    confirmRemoveMember,
    handleDeleteEvent,
    rsvpData,
    handleCreateMeetingTimes,
    handleDeleteMeetingTime,
    isCreatingMeetingTimes,
    handleAddTask,
    handleDeleteTask,
    handleCompleteTask,
    handleAddMilestone,
    handleDeleteMilestone,
    handleCompleteMilestone,
    handleUpdateEvent,
    refetchEvent,
  } = useEventDashboard({
    eventId,
    userId,
  });

  // Get real-time chat data
  const { members: activeChatMembers } = useWrappedChat({ eventId });
  const slideouts = useEventSlideouts();
  const [isDeleteEventDialogOpen, setIsDeleteEventDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<DateTime | undefined>();
  const [currentMonth, setCurrentMonth] = useState(DateTime.now().startOf("month"));

  const handleDateSelect = useCallback((date: DateTime | undefined) => {
    setSelectedDate(date);
    if (date) {
      setCurrentMonth(date.startOf("month"));
    }
  }, []);
  const [selectedMeetingTime, setSelectedMeetingTime] = useState<
    | {
        id: string;
        startTime: string;
        endTime: string;
      }
    | undefined
  >();

  // Memoized function to get RSVP data for selected meeting time
  const selectedTimeRsvpData = useMemo(() => {
    if (!selectedMeetingTime || !rsvpData) {
      return null;
    }

    const rsvpsForTime = rsvpData.filter((rsvp) => rsvp.meetingTime.id === selectedMeetingTime.id);

    return {
      attendees: rsvpsForTime.map((rsvp) => {
        // Find the member data to get vetted status
        const memberInfo = memberData?.members.find((member) => member.id === rsvp.user.id);
        return {
          email: rsvp.user.email || "",
          id: rsvp.user.id,
          isVetted: Boolean(memberInfo?.isVetted),
          name: `${rsvp.user.firstName ?? ""} ${rsvp.user.lastName ?? ""}`.trim(),
        };
      }),
      isUserAttending: rsvpsForTime.some((rsvp) => rsvp.user.id === userId),
      meetingTime: {
        beginMeetingTime: rsvpsForTime[0]?.meetingTime.beginMeetingTime,
        endMeetingTime: rsvpsForTime[0]?.meetingTime.endMeetingTime,
        id: selectedMeetingTime.id,
      },
      totalAttendees: rsvpsForTime.length,
    };
  }, [selectedMeetingTime, rsvpData, userId, memberData?.members]);

  const handleMeetingTimeDelete = useCallback(
    (meetingTimeId: string) => {
      handleDeleteMeetingTime(meetingTimeId);
      // Clear selected meeting time if it was the one that was deleted
      if (selectedMeetingTime?.id === meetingTimeId) {
        setSelectedMeetingTime(undefined);
      }
    },
    [handleDeleteMeetingTime, selectedMeetingTime],
  );

  const { handleManageRules, handleSaveRules, isRulesDialogOpen, onCloseRulesDialog } = useEventRules({
    eventId,
    rules: event?.rules ?? [],
  });

  const { handleUpdateMeetingTime } = useEventMeetingTimes({
    event,
    eventId,
    refetchEvent: async () => {
      await refetchEvent();
    },
  });

  const { handleManageSeries, handleSaveSeries, isSeriesDialogOpen, onCloseSeriesDialog } = useEventSeries({
    eventId,
    refetchEvent,
  });

  const { handleJoinEvent, handleJoinWithRules, isRulesAgreementModalOpen, onCloseRulesAgreementModal } = useEventJoin({
    onRequestJoin: handleRequestJoin,
    rules: event?.rules ?? [],
  });

  if (isLoading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: "100vh", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{STRINGS.EVENT_NOT_FOUND}</Typography>
      </Box>
    );
  }

  // Only show error if it's not a permission error
  if (error && error !== "Unauthorized") {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{STRINGS.ERROR_LOADING}</Typography>
      </Box>
    );
  }

  return (
    <>
      <ConfirmDeleteDialog
        open={Boolean(pendingRemovalMember)}
        onClose={() => handleRemoveMember("")}
        onConfirm={confirmRemoveMember}
        itemType="member"
        itemName={pendingRemovalMember?.name || ""}
        itemDescription={`${STRINGS.FROM} ${event.name}`}
      />
      <ConfirmDeleteDialog
        open={isDeleteEventDialogOpen}
        onClose={() => setIsDeleteEventDialogOpen(false)}
        onConfirm={handleDeleteEvent}
        itemType="event"
        itemName={event.name}
        itemDescription={event.description}
      />
      <ConfirmCancelRequestDialog
        open={isCancelRequestDialogOpen}
        onClose={() => setIsCancelRequestDialogOpen(false)}
        onConfirm={confirmCancelRequest}
        itemType="event"
        itemName={event.name}
      />
      <EventRulesDialog
        open={isRulesDialogOpen}
        onClose={onCloseRulesDialog}
        onSave={handleSaveRules}
        rules={event.rules ?? []}
      />
      <EventSeriesDialog
        open={isSeriesDialogOpen}
        onClose={onCloseSeriesDialog}
        onSave={handleSaveSeries}
        series={event?.series ?? []}
      />
      <EventRulesAgreementModal
        open={isRulesAgreementModalOpen}
        onClose={onCloseRulesAgreementModal}
        onJoin={handleJoinWithRules}
        rules={event.rules ?? []}
        isPrivate={!event.isPublic}
      />
      <EventDashboardTemplate
        event={event}
        imageUrl={imageUrl}
        status={status}
        onImageChange={uploadImage}
        onJoinEvent={async () => handleJoinEvent?.()}
        onLeaveEvent={handleLeave}
        onCancelRequest={async () => handleCancelRequest?.()}
        onShareEvent={handleShareEvent}
        onDeleteEvent={() => setIsDeleteEventDialogOpen(true)}
        onManageRules={handleManageRules}
        onManageSeries={handleManageSeries}
        onAddMember={handleAddMember}
        members={memberData?.members ?? []}
        requests={memberData?.requests ?? []}
        onRemoveMember={handleRemoveMember}
        onApproveMember={handleApproveMember}
        onRejectMember={handleRejectMember}
        onAddFacilitator={handleAddFacilitator}
        onRemoveFacilitator={handleRemoveFacilitator}
        onAddOwner={handleAddOwner}
        // Resource management
        onAddResource={resourceManagement.saveResource}
        onEditResource={resourceManagement.saveResource}
        onDeleteResource={resourceManagement.deleteResource}
        // RSVP management
        isUserAttending={selectedTimeRsvpData?.isUserAttending ?? false}
        onRSVP={handleRsvp}
        onCancelRSVP={handleUnrsvp}
        recentAttendees={selectedTimeRsvpData?.attendees ?? []}
        rsvpData={rsvpData?.map((rsvp) => ({
          id: rsvp.id,
          meetingTime: {
            beginMeetingTime: rsvp.meetingTime.beginMeetingTime,
            endMeetingTime: rsvp.meetingTime.endMeetingTime,
            id: rsvp.meetingTime.id,
          },
          user: {
            email: rsvp.user.email ?? null,
            firstName: rsvp.user.firstName ?? null,
            id: rsvp.user.id,
            lastName: rsvp.user.lastName ?? null,
          },
        }))}
        // Chat member count
        chatMemberCount={activeChatMembers.length}
        // Slideout state
        resourcesSlideoutOpen={slideouts.resources.isOpen}
        membersSlideoutOpen={slideouts.members.isOpen}
        chatSlideoutOpen={slideouts.chat.isOpen}
        onResourcesClose={slideouts.resources.close}
        onMembersClose={slideouts.members.close}
        onChatClose={slideouts.chat.close}
        onResourcesClick={slideouts.resources.open}
        onMembersClick={slideouts.members.open}
        onChatClick={slideouts.chat.open}
        // Event times slideout state
        eventTimesSlideoutOpen={slideouts.eventTimes.isOpen}
        onEventTimesClose={slideouts.eventTimes.close}
        onEventTimesClick={slideouts.eventTimes.open}
        onUpdateMeetingTime={event.userIsFacilitator ? handleUpdateMeetingTime : undefined}
        availableSeries={event.series}
        // Selected meeting time
        selectedMeetingTime={selectedMeetingTime}
        onSelectMeetingTime={setSelectedMeetingTime}
        // Selected date
        selectedDate={selectedDate}
        onSelectDate={handleDateSelect}
        // Current month
        currentMonth={currentMonth}
        onMonthChange={setCurrentMonth}
        // Meeting time management
        onCreateMeetingTimes={handleCreateMeetingTimes}
        onDeleteMeetingTime={handleMeetingTimeDelete}
        isCreatingMeetingTimes={isCreatingMeetingTimes}
        // Task management
        onAddTask={handleAddTask}
        onDeleteTask={handleDeleteTask}
        onCompleteTask={handleCompleteTask}
        // Milestone management
        onAddMilestone={handleAddMilestone}
        onDeleteMilestone={handleDeleteMilestone}
        onCompleteMilestone={handleCompleteMilestone}
        onUpdateEvent={handleUpdateEvent}
        bubbleSelectItems={myBubbles?.me.bubbles.map(({ id, name }) => ({ id, value: name })) ?? []}
      />
    </>
  );
};
